import type { ProductType } from '@periodica/products';
import axios from 'axios';
import { API_URL, PROJECT_API_URL, headers } from './defaults';

/**
 * базовый axios клиент для работы с API
 */
const axiosClient = axios.create({
  baseURL: API_URL,
  headers,
});

const axiosProjectClient = axios.create({
  baseURL: PROJECT_API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

/**
 * delivery API
 */

export interface ICityDto {
  id: number;
  name: string;
  displayOrder: number;
  region: string;
  lat?: string;
  lng?: string;
  courier: boolean;
  pickup: boolean;
}

interface ICityDomain {
  id: number;
  name: string;
  display_order: number;
  region: string;
  lat: string;
  lng: string;
  courier: boolean;
  pickup: boolean;
}

function normalizeCity(t: ICityDomain): ICityDto {
  return {
    id: t.id,
    name: t.name,
    displayOrder: t.display_order,
    region: t.region,
    lat: t.lat,
    lng: t.lng,
    courier: t.courier,
    pickup: t.pickup,
  };
}

/**
 *
 * получить список городов в которые возможна доставка
 *
 * дока:
 * https://test-api1.periodica.press/swagger_ui/index.html#/delivery/getDeliveryCities
 */

export async function fetchCities() {
  const response = await axiosClient.get<ICityDomain[]>(`/delivery/cities`);
  const { data } = response;
  const cities = data.map((apiCity) => normalizeCity(apiCity));

  return cities;
}

export type PaymentMethod = 'cash' | 'online';

export interface IPickupPointDto {
  id: number;
  title: string;
  address: string;
  metro: string;
  deliveryProviderName: string;
  openingHours: string;
  howToGet: string;
  paymentMethods: PaymentMethod[];
  lat: number;
  lng: number;
}

interface IPickupPointDomain {
  id: number;
  title: string;
  address: string;
  metro: string;
  delivery_provider_name: string;
  opening_hours: string;
  how_to_get: string;
  payment_methods: PaymentMethod[];
  lat: number;
  lng: number;
}

function normalizePickupPoint(t: IPickupPointDomain): IPickupPointDto {
  return {
    id: t.id,
    title: t.title,
    metro: t.metro,
    address: t.address,
    deliveryProviderName: t.delivery_provider_name,
    openingHours: t.opening_hours,
    howToGet: t.how_to_get,
    paymentMethods: t.payment_methods,
    lat: t.lat,
    lng: t.lng,
  };
}

/**
 *
 * получить список доступных точек самовывоза для города
 *
 * дока:
 * https://test-api1.periodica.press/swagger_ui/index.html#/delivery/getDeliveryCitiesIdPickupPoints
 */

export async function fetchPickupPoints(id: number) {
  const response = await axiosClient.get<IPickupPointDomain[]>(
    `/delivery/cities/${id}/pickup-points`
  );
  const { data } = response;
  return data.map((apiPoint) => normalizePickupPoint(apiPoint));
}

export interface ICartProduct {
  count: number;
  size: string;
  type: ProductType;
}

export type IDeliveryCalculatorResponseDto = {
  sum: number;
  days: number;
  date: string;
};

/**
 * рассчитывает стоимость и время доставки для указанных товаров в указанный город и пвз
 * если не указывать `pickupPointId`, будет рассчитана цена курьерской доставки
 *
 * дока:
 * https://test-api1.periodica.press/swagger_ui/index.html#/delivery/putDeliveryCalculator
 */

export async function deliveryCalculator({
  items,
  cityId,
  pickupPointId,
}: {
  items: ICartProduct[];
  cityId: number;
  pickupPointId?: number;
}) {
  const response = await axiosClient.put<{
    result: IDeliveryCalculatorResponseDto;
  }>(`/delivery/calculator`, {
    items,
    city_id: cityId,
    pickup_point_id: pickupPointId,
  });

  return response.data.result;
}

/**
 * suggestions API
 *
 * https://test-api1.periodica.press/swagger_ui/index.html#/delivery/postDeliveryAddressSuggestions
 *
 */

type DaDataInfo = {
  country: string | null;
  region: string | null;
  region_type: string | null;
  region_with_type: string | null;
  area: string | null;
  area_type: string | null;
  area_with_type: string | null;
  sub_area: string | null;
  sub_area_type: string | null;
  sub_area_with_type: string | null;
  city: string | null;
  city_type: string | null;
  city_with_type: string | null;
  city_district: string | null;
  city_district_type: string | null;
  city_district_with_type: string | null;
  settlement: string | null;
  settlement_type: string | null;
  settlement_with_type: string | null;
  street: string | null;
  street_type: string | null;
  street_with_type: string | null;
  house: string | null;
  house_type: string | null;
  block: string | null;
  block_type: string | null;
  flat: string | null;
  flat_type: string | null;
  room: string | null;
  room_type: string | null;
  level:
    | '-1' // иностранный адрес или уровень не определён
    | '0' // страна
    | '1' // регион
    | '3' // район
    | '4' // город
    | '5' // район города
    | '6' // населённый пункт
    | '7' // улица
    | '8' // дом
    | '9' // квартира или комната
    | '65' // планировочная структура
    | '75'; // земельный участок
};

type AddressSuggestion = {
  unrestricted_value: string;
  value: string;

  data: DaDataInfo;
};

export async function getDeliveryAddressSuggestions({
  cityId,
  query,
  signal,
}: {
  cityId: number;
  query: string;
  signal?: AbortSignal;
}) {
  const response = await axiosClient.post<{ result: AddressSuggestion[] }>(
    `/delivery/address/suggestions`,
    {
      city_id: cityId,
      query,
    },
    { signal }
  );

  return response.data.result;
}

/**
 * promocode API
 */

/**
 * валидация промокода
 * дока: https://test-api1.periodica.press/swagger_ui/index.html#/promocode/postPromocode
 */

export interface IPromocodeValidationResponseDomain {
  discount: number;
  type: 'percent' | 'fixed';
  first_order: boolean;
  min_sum?: number;
  kind?: 'delivery' | 'regular' | 'certificate';
  usage_type: 'single' | 'multiple';
}
export interface IPromocodeValidationResponseDto {
  discount: number;
  firstOrder: boolean;
  type: 'percent' | 'fixed';
  kind: 'regular' | 'delivery' | 'certificate';
  usageType?: 'single' | 'multiple';
  minSum?: number;
}

// тут все ошибки с заделом на то, чтобы перевести все на snake_case
export type PromocodeValidationError =
  | 'not-valid'
  | 'not_valid'
  | 'empty'
  | 'repeat-purchase'
  | 'repeat_purchase'
  | 'disabled'
  | 'need-login'
  | 'need_login'
  | 'expired'
  | 'special_cannot_be_applied'
  | 'no_applicable_products'
  | 'other_user'
  | 'self_referral';

// остальные апи обновим позже
export type PromocodeSfzvValidationError = PromocodeValidationError | 'not_applicable_to_sfzv';

export interface IPromocodeValidationErrorDto {
  error: PromocodeValidationError;
}
export interface IPromocodeSfzvValidationErrorDto {
  error: PromocodeSfzvValidationError;
}

function normalizePromocode(
  t: IPromocodeValidationResponseDomain
): IPromocodeValidationResponseDto {
  return {
    discount: t.discount,
    type: t.type,
    firstOrder: t.first_order,
    minSum: t.min_sum,
    kind: t.kind,
    usageType: t.usage_type,
  } as IPromocodeValidationResponseDto;
}

function normalizePromocodeError(error: unknown): IPromocodeValidationErrorDto {
  return error as IPromocodeValidationErrorDto;
}
function normalizePromocodeSfzvError(error: unknown): IPromocodeSfzvValidationErrorDto {
  return error as IPromocodeSfzvValidationErrorDto;
}

export async function validatePromocode(
  promocode: string,
  products: ICartProduct[],
  userId?: number | undefined,
  signal?: AbortSignal
) {
  try {
    const response = await axiosClient.post<IPromocodeValidationResponseDomain>(
      `/promocode`,
      { promocode, products, user_id: userId },
      { headers: { 'Accept-Version': 'v12' }, signal }
    );
    const { data } = response;
    return normalizePromocode(data);
  } catch (error: any) {
    type ValidationError = { error: string };

    if (axios.isAxiosError<ValidationError>(error)) {
      const reason = error.response?.data;
      if (reason && reason.error) {
        return normalizePromocodeError(reason);
      }
    }

    const errMessage = `API Client validatePromocode transport error, status: ${error?.response?.status}, message: ${error?.response?.data?.message}`;

    throw new Error(errMessage, {
      cause: error as Error,
    });
  }
}
// отдельное апи для сертиков в СФЗВ, которое принимает сертики, у которых есть userID
// так как на сайте нет авторизации и нет простого способа проверить привязку пользователя
export async function validateSfzvPromocode(certificate: string, signal?: AbortSignal) {
  try {
    const response = await axiosClient.post<IPromocodeValidationResponseDomain>(
      `/promocode/sfzv-certificate`,
      { certificate },
      { headers: { 'Accept-Version': 'v12' }, signal }
    );
    const { data } = response;
    return normalizePromocode(data);
  } catch (error: any) {
    type ValidationError = { error: string };

    if (axios.isAxiosError<ValidationError>(error)) {
      const reason = error.response?.data;
      if (reason && reason.error) {
        return normalizePromocodeSfzvError(reason);
      }
    }

    const errMessage = `API Client validateSfzvPromocode transport error, status: ${error?.response?.status}, message: ${error?.response?.data?.message}`;

    throw new Error(errMessage, {
      cause: error as Error,
    });
  }
}

/**
 * products API
 */

/**
 * получить ID продукта по типу и размеру
 * https://test-api1.periodica.press/swagger_ui/index.html#/products/getProductsTypeSize
 */

export async function getProductIdByTypeAndSize({
  type,
  size,
}: {
  type: ProductType;
  size: string;
}) {
  try {
    const response = await axiosClient.get<{ result: { id: number } }>(`/products/type-size`, {
      params: { type, size },
    });
    return response.data.result.id;
  } catch (error) {
    // не нашли такой продукт, возвращаем null без искючения
    if (axios.isAxiosError(error) && error.response?.status === 404) {
      return null;
    }

    throw error;
  }
}

interface Template {
  id: number;
  type: ProductType;
  number_of_photos: [number, number];
  thumbnail_url: string;
  description: string;
  front_page_layout: string;
  back_page_layout: string;
  layouts: string[];
  preview: string;
  preview_url: string;
  has_text: boolean;
  base_price: number;
  price_per_photo: number;
  version: number;
  full_cover: string;
  size: string;
  name: string;
  set_title: string;
  set_image_url: string;
  allow_print_export: boolean;
}

interface Layout {
  id: number;
  layout: string;
}

type ProductState = 'enabled' | 'disabled' | 'prerelease' | 'will_be_removed' | 'removed';

interface ProductInfo {
  id: number;
  type: ProductType;
  size: string;
  description: string;
  state: ProductState;
  layouts: number[];
  templates: Template[];
}

type Products = {
  products: ProductInfo[];
  layouts: Layout[];
};

/**
 * получить продукты по типу
 * https://test-api1.periodica.press/swagger_ui/index.html#/products/getProductsType
 */

export async function getProductsByType({ type }: { type: ProductType }) {
  const response = await axiosClient.get<Products>(`/products/type/${type}`);
  return response.data;
}

/**
 * получить продукт по ID
 * https://test-api1.periodica.press/swagger_ui/index.html#/products/getProductsId
 */

export async function getProductsById({ id }: { id: ProductInfo['id'] }) {
  const response = await axiosClient.get<Products>(`/products/${id}`);
  return response.data;
}

type RelatedProduct = {
  base_price: Template['base_price'];
  description: ProductInfo['description'];
  id: ProductInfo['id'];
  pages_number: number;
  template_svg: string;
};

/**
 * получить продукты с одинаковым размером блока, на который можно переключиться текущему проект
 * https://test-api1.periodica.press/swagger_ui/index.html#/products/getProductsIdRelated
 */

export async function getRelatedProducts({ id }: { id: ProductInfo['id'] }) {
  const response = await axiosClient.get<{ products: RelatedProduct[] }>(`/products/${id}/related`);
  return response.data;
}

type RelatedGroup = ProductInfo['id'][];

type SimpleProducts = {
  products: (Pick<ProductInfo, 'id' | 'type' | 'size' | 'state' | 'description'> &
    Pick<RelatedProduct, 'pages_number' | 'base_price' | 'template_svg'> & {
      create_url: string;
    })[];
  related: RelatedGroup[];
};

/**
 * получить продукты без шаблонов для валидации статуса и обновления цены / названий
 * https://test-api1.periodica.press/swagger_ui/index.html#/products/getProductsSimple
 */

export async function getProductsInfo() {
  const response = await axiosClient.get<SimpleProducts>(`/products/simple`);
  return response.data;
}

/**
 * Конвертирует SVG в изображение и возвращает URL
 * https://test-api1.periodica.press/swagger_ui/index.html#/preview/postPreview
 */

export async function generatePreview(params: { templateId: number; svg: string }) {
  const response = await axiosClient.post<string>(`/preview`, {
    template_id: params.templateId,
    svg: params.svg,
  });

  return response.data;
}

/**
 * CERTIFICATES API
 */

/**
 * Получение сертификата по токену
 * https://test-api1.periodica.press/swagger_ui/index.html#/projects/postV2Projects
 */

export interface ICertificateDto {
  balance: number;
  certificateId: string;
  dateFrom: string;
  dateTo: string;
  initialBalance: number;
  pdfUrl: string;
  previewUrl: string;
}

interface ICertificateDomain {
  balance: number;
  certificate_id: string;
  date_from: string;
  date_to: string;
  initial_balance: number;
  pdf_url: string;
  preview_url: string;
}

function normalizeCertificate(t: ICertificateDomain): ICertificateDto {
  return {
    balance: t.balance,
    certificateId: t.certificate_id,
    dateFrom: t.date_from,
    dateTo: t.date_to,
    initialBalance: t.initial_balance,
    pdfUrl: t.pdf_url,
    previewUrl: t.preview_url,
  };
}

export async function getCertificateByToken(token: string) {
  try {
    const { data } = await axiosClient.get<ICertificateDomain>(`gift/?token=${token}`);
    return normalizeCertificate(data);
  } catch (error) {
    // возвращаем только серверные ошибки и сетевые
    if (axios.isAxiosError(error) && error.response?.status && error.response.status <= 500) {
      return null;
    }
    throw error;
  }
}

/**
 * PROJECTS API
 */

export type SavedProjectInfoDto<ProjectType> = {
  project_id: string;

  version: string; // sha-1 digest from project
  last_saved_version_on_client?: string; // previous sha-1 digest from project

  name: string;
  pages_count: number;
  base_price: number;
  product_code: ProductType;

  full_data: ProjectType;

  metadata: {
    createdAt?: number;

    preview: {
      svg: string;
      width: number;
      height: number;
    };

    conflictInfo?: {
      date: number;
      browser?: string;
      os?: string;
    };

    productId?: number;
    title?: string;
  };
};

/**
 * Получение проекта по id c токеном
 * https://dev-projects.periodica.press/swagger_ui/index.html#/projects/Periodica
 */

export async function getProjects({ projectId, token }: { projectId: string; token: string }) {
  const response = await axiosProjectClient.get(`/v1/projects/${projectId}`, {
    headers: {
      authorization: `Bearer ${token}`,
    },
  });
  return response.data;
}

/**
 * Сохранение проекта с токеном
 * https://dev-projects.periodica.press/swagger_ui/index.html#/projects/postV2Projects
 */

export async function updateProject({
  projectData,
  token,
}: {
  projectData: SavedProjectInfoDto<any>;
  token: string;
}): Promise<void> {
  await axiosProjectClient.post(`/v2/projects`, projectData, {
    headers: {
      authorization: `Bearer ${token}`,
    },
  });
}

/**
 * Хелпер который позволяет понять что запрос затротлен и не нужно его повторять какое-то время
 */

export function isRateLimitError(error: unknown): boolean {
  if (axios.isAxiosError(error)) {
    if (error.response?.status === 429) {
      return true;
    }

    if (error.response?.data?.error?.toLowerCase() === 'throttled') {
      return true;
    }
  }

  return false;
}
